// Chat Widget editor (PRD §4.4, Bot Manager → Chat Widget card). Configure the
// floating WhatsApp button, preview it live, and copy the embed snippet.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '../lib/i18n';
import { cn } from '../lib/cn';
import Toggle from '../components/ui/Toggle';
import {
  useChatWidget, saveChatWidget, widgetSnippet, DEFAULT_WIDGET,
  type ChatWidgetConfig, type WidgetPosition,
} from '../lib/chatWidget';

const inputCls = 'w-full rounded-[9px] border border-slate-200 px-3 py-2 text-sm outline-none focus:border-primary';

export default function ChatWidget() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { config, loading } = useChatWidget();
  const [c, setC] = useState<ChatWidgetConfig>(DEFAULT_WIDGET);
  const [hydrated, setHydrated] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [copied, setCopied] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => { if (!loading && !hydrated) { setC(config); setHydrated(true); } }, [loading, hydrated, config]);

  async function save() {
    setSaving(true); setErr(null); setSaved(false);
    try { await saveChatWidget(c); setSaved(true); window.setTimeout(() => setSaved(false), 1800); }
    catch (e) { setErr((e as Error).message); } finally { setSaving(false); }
  }

  async function copy() {
    try { await navigator.clipboard.writeText(widgetSnippet(c)); setCopied(true); window.setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
  }

  if (loading || !hydrated) return <div className="py-10 text-center text-sm text-ink-3">…</div>;

  return (
    <div>
      <div className="mb-1 flex items-center gap-2 text-sm text-ink-3">
        <button type="button" onClick={() => navigate('/bots')} className="hover:text-ink">{t('bot.title')}</button>
        <span>/</span><span className="font-semibold text-ink">{t('widget.title')}</span>
      </div>
      <h1 className="text-[22px] font-extrabold text-ink">{t('widget.title')}</h1>
      <p className="mb-5 text-ink-3">{t('widget.subtitle')}</p>

      <div className="grid gap-5 lg:grid-cols-[1fr_360px]">
        {/* Config */}
        <div className="space-y-4">
          <div className="rounded-card border border-slate-200 bg-white p-5 shadow-sm">
            <div className="flex items-center justify-between border-b border-slate-200 pb-3">
              <b className="text-sm text-ink">{t('widget.enable')}</b>
              <Toggle on={c.enabled} onChange={(v) => setC({ ...c, enabled: v })} label={t('widget.enable')} />
            </div>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <label className="block">
                <span className="mb-1 block text-xs font-bold text-ink-2">{t('widget.phone')}</span>
                <input value={c.phoneE164} onChange={(e) => setC({ ...c, phoneE164: e.target.value })} placeholder="+9687..." aria-label={t('widget.phone')} className={inputCls} />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-bold text-ink-2">{t('widget.greeting')}</span>
                <input value={c.greeting} onChange={(e) => setC({ ...c, greeting: e.target.value })} aria-label={t('widget.greeting')} className={inputCls} />
              </label>
              <label className="block sm:col-span-2">
                <span className="mb-1 block text-xs font-bold text-ink-2">{t('widget.prefill')}</span>
                <input value={c.prefill} onChange={(e) => setC({ ...c, prefill: e.target.value })} aria-label={t('widget.prefill')} className={inputCls} />
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-bold text-ink-2">{t('widget.color')}</span>
                <div className="flex items-center gap-2">
                  <input type="color" value={c.color} onChange={(e) => setC({ ...c, color: e.target.value })} aria-label={t('widget.color')} className="h-9 w-12 rounded border border-slate-200" />
                  <input value={c.color} onChange={(e) => setC({ ...c, color: e.target.value })} aria-label={t('widget.color')} className={cn(inputCls, 'flex-1')} />
                </div>
              </label>
              <label className="block">
                <span className="mb-1 block text-xs font-bold text-ink-2">{t('widget.position')}</span>
                <select value={c.position} onChange={(e) => setC({ ...c, position: e.target.value as WidgetPosition })} className={cn(inputCls, 'bg-white')}>
                  <option value="right">{t('widget.right')}</option>
                  <option value="left">{t('widget.left')}</option>
                </select>
              </label>
            </div>
            {err && <div className="mt-3 rounded-[9px] bg-red-50 px-3 py-2 text-xs font-semibold text-red-600">{err}</div>}
            <button type="button" onClick={() => void save()} disabled={saving || !c.phoneE164.trim()} className="mt-4 rounded-[9px] bg-primary px-5 py-2.5 text-[13px] font-bold text-white disabled:opacity-40">
              {saving ? '…' : saved ? t('auto.saved') : t('auto.save')}
            </button>
          </div>

          {/* Embed snippet */}
          <div className="rounded-card border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-2 flex items-center justify-between">
              <b className="text-sm text-ink">{t('widget.embed')}</b>
              <button type="button" onClick={() => void copy()} className="rounded-[9px] border border-slate-200 px-3 py-1.5 text-xs font-bold text-ink">
                {copied ? t('set.api.copied') : t('set.api.copy')}
              </button>
            </div>
            <p className="mb-2 text-xs text-ink-3">{t('widget.embedNote')}</p>
            <pre className="max-h-64 overflow-auto rounded-[9px] bg-surface p-3 text-[11px] leading-relaxed text-ink-2"><code>{widgetSnippet(c)}</code></pre>
          </div>
        </div>

        {/* Live preview */}
        <div className="lg:sticky lg:top-20 lg:self-start">
          <div className="relative h-[360px] overflow-hidden rounded-card border border-slate-200 bg-slate-50 shadow-inner">
            <div className="border-b border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-ink-3">{t('widget.preview')}</div>
            <div className="p-4 text-xs text-ink-3">{t('widget.previewNote')}</div>
            <a
              href={`https://wa.me/${c.phoneE164.replace(/\D/g, '')}?text=${encodeURIComponent(c.prefill)}`}
              target="_blank" rel="noopener noreferrer"
              title={c.greeting}
              style={{ background: c.color, [c.position]: '20px' } as React.CSSProperties}
              className="absolute bottom-5 grid h-14 w-14 place-items-center rounded-full shadow-lg"
            >
              <svg width="30" height="30" viewBox="0 0 24 24" fill="white"><path d="M12 2a10 10 0 0 0-8.6 15l-1.3 4.7 4.8-1.3A10 10 0 1 0 12 2zm0 18a8 8 0 0 1-4-1.1l-.3-.2-2.9.8.8-2.8-.2-.3A8 8 0 1 1 12 20z" /></svg>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
